import { fetchUserAttributes } from 'aws-amplify/auth';
import { Hub, HubCapsule } from 'aws-amplify/utils';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { AppUser, AuthContext, createAppUser } from '~/utils';

type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

interface AuthState {
    status: AuthStatus;
    user: AppUser | null;
}

interface AuthProviderProps {
    children: (authStatus: AuthStatus) => React.ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
    const [{ status, user }, setState] = useState<AuthState>({
        status: 'loading',
        user: null,
    });

    useEffect(() => {
        confirmSignIn(setState);
    }, []);

    useEffect(() => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const authListener = async (data: HubCapsule<any, any>) => {
            switch (data.payload.event) {
                case 'signedIn':
                    return confirmSignIn(setState);
                case 'signedOut':
                case 'signedIn_failure':
                    return setState({ status: 'unauthenticated', user: null });
                default:
                    return;
            }
        };
        const removeListener = Hub.listen('auth', authListener);

        return () => {
            removeListener();
        };
    }, []);

    return <AuthContext.Provider value={user}>{children(status)}</AuthContext.Provider>;
};

const confirmSignIn = async (setState: Dispatch<SetStateAction<AuthState>>) => {
    const user = await fetchUserAttributes().catch(error => console.error('Failed to fetch user attributes', error));

    if (!user) return setState({ status: 'unauthenticated', user: null });

    return setState({ status: 'authenticated', user: createAppUser(user) });
};
